import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Eye, FileText, Trash2, Loader2 } from "lucide-react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";

const STATUS_LABEL = { draft: "Draf", scheduled: "Terjadwal", published: "Terbit", trashed: "Tong Sampah" };

export default function AdminArticles() {
  const [data, setData] = useState({ items: [] });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (q) qs.set("q", q);
    api.get(`/admin/articles?${qs}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        setData({ items: [] });
        if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q]);

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(id + action);
    try {
      await api.post(`/admin/articles/${id}/action/${action}`, {});
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const duplicate = async (id) => {
    setBusy(id + "dup");
    try {
      await api.post(`/admin/articles/${id}/duplicate`);
      load();
    } catch (err) {
      if (err.response?.status !== 401) alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus permanen? Tindakan ini tidak dapat dibatalkan.")) return;
    setBusy(id + "del");
    try {
      await api.delete(`/admin/articles/${id}?force=true`);
      load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-articles-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-3xl text-ink">Tulisan</h1>
        <Link to="/admin/tulisan/baru" data-testid="admin-new-article-button" className="btn-primary !min-h-[40px]">Tulis Baru</Link>
      </div>

      <div className="flex gap-3 mt-6 flex-wrap">
        <select data-testid="admin-article-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="field-input !min-h-[40px] !w-auto">
          <option value="">Semua status</option>
          <option value="published">Terbit</option>
          <option value="draft">Draf</option>
          <option value="scheduled">Terjadwal</option>
          <option value="trashed">Tong Sampah</option>
        </select>
        <input data-testid="admin-article-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari judul…" className="field-input !min-h-[40px] !w-auto flex-1 max-w-xs" />
      </div>

      {error ? (
        <p role="alert" className="text-enji text-sm mt-8">{error}</p>
      ) : loading ? (
        <p className="text-sub text-sm mt-8">Memuat…</p>
      ) : data.items.length === 0 ? (
        <p className="text-sub text-sm mt-8">Tidak ada tulisan pada filter ini.</p>
      ) : (
        <div className="mt-6 bg-surface border border-line rounded-xl overflow-hidden">
          <ul className="divide-y divide-line">
            {data.items.map((a) => (
              <li key={a.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-article-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink truncate">{a.title}</p>
                  <p className="text-[13px] text-faint mt-1">
                    {a.category || "Tanpa kategori"} · {STATUS_LABEL[a.status] || a.status} · diubah {formatDate(a.updated_at)}
                    {a.featured ? " · unggulan" : ""}
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {a.status === "published" && (
                    <Link to={`/tulisan/${a.slug}`} target="_blank" data-testid={`admin-preview-${a.id}`} title="Lihat di situs" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Eye size={15} /></Link>
                  )}
                  {a.status !== "trashed" && a.status !== "published" && (
                    <button onClick={() => act(a.id, "published")} data-testid={`admin-publish-${a.id}`} title="Terbitkan" className="px-3 h-9 rounded-md bg-enji text-white text-[13px] font-semibold hover:opacity-90">Terbitkan</button>
                  )}
                  {a.status === "published" && (
                    <button onClick={() => act(a.id, "draft")} data-testid={`admin-unpublish-${a.id}`} title="Jadikan draf" className="px-3 h-9 rounded-md border border-line text-[13px] font-semibold text-sub hover:text-ink">Draft</button>
                  )}
                  <Link to={`/admin/tulisan/${a.id}`} data-testid={`admin-edit-${a.id}`} className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji" title="Sunting"><FileText size={15} /></Link>
                  {a.status !== "trashed" && (
                    <button onClick={() => duplicate(a.id)} data-testid={`admin-duplicate-${a.id}`} title="Duplikasi" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Copy size={15} /></button>
                  )}
                  {a.status !== "trashed" && (
                    <button onClick={() => act(a.id, "trashed", "Pindahkan ke tong sampah?")} data-testid={`admin-trash-${a.id}`} title="Tong sampah" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Trash2 size={15} /></button>
                  )}
                  {a.status === "trashed" && (
                    <>
                      <button onClick={() => act(a.id, "draft")} data-testid={`admin-restore-${a.id}`} className="px-3 h-9 rounded-md border border-line text-[13px] font-semibold text-sub">Pulihkan</button>
                      <button onClick={() => del(a.id)} data-testid={`admin-delete-forever-${a.id}`} className="px-3 h-9 rounded-md bg-enji text-white text-[13px] font-semibold">Hapus permanen</button>
                    </>
                  )}
                  {busy === a.id && <Loader2 size={15} className="animate-spin self-center" />}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
